/**
 * Small categorical provenance chip rendered beside a metadata field label:
 * AI-written · Template fallback (review) · Your edit · (nothing when not generated).
 *
 * Honest by construction — categorical state only, NEVER a confidence number,
 * NEVER a model name (both are shipped constraints).
 *
 * ⚠ INTERFACE IS LOCKED. Sibling sessions import and render
 * `<MetadataFieldChip state={fieldStates[field]} />` — do NOT change the props.
 */
import type { JSX } from 'react';
import { Lock, Sparkles, AlertTriangle } from 'lucide-react';
import type { MetadataFieldState } from '../postgenEvidence';
import { Pill, type PillTone } from '../_primitives';

export interface MetadataFieldChipProps {
  /** Field state from readPostgenEvidence. Undefined → renders nothing. */
  state?: MetadataFieldState;
}

/**
 * Categorical provenance chip. Source → tone + label:
 *   'ai'           → coral        "AI"
 *   'fallback'     → warning      "Template default — review"
 *   'user-edited'  → success      "Your edit" (lock idiom)
 *   'not-generated' / undefined   → null (chip renders nothing)
 */
export function MetadataFieldChip({ state }: MetadataFieldChipProps): JSX.Element | null {
  if (!state) return null;

  let tone: PillTone;
  let label: string;
  let icon: JSX.Element | undefined;

  switch (state.source) {
    case 'ai':
      tone = 'coral';
      label = 'AI';
      icon = <Sparkles className="size-2.5" />;
      break;
    case 'fallback':
      tone = 'warning';
      label = 'Template default — review';
      icon = <AlertTriangle className="size-2.5" />;
      break;
    case 'user-edited':
      tone = 'success';
      label = 'Your edit';
      icon = <Lock className="size-2.5" />;
      break;
    case 'not-generated':
    default:
      return null;
  }

  return (
    <Pill tone={tone} size="sm" icon={icon}>
      {label}
    </Pill>
  );
}
